import React, { useState } from 'react';
import moment from 'moment';
import DatePicker, { registerLocale } from 'react-datepicker';
import { fr } from 'date-fns/locale';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import 'react-datepicker/dist/react-datepicker.css';

import './AppointmentRequestsTab.scss';

import { ContentBlock } from 'components/ContentBlock/ContentBlock';
import { Button } from 'components/Button/Button';
import { Chip, ChipColor } from 'components/Chip/Chip';
import { Modal } from 'components/Modal/Modal';
import { Person } from 'services/person/person.types';
import { Appointment } from 'services/appointment/appointment.types';
import { useAppointmentRequestsTabVM } from './useAppointmentRequestsTabVM';

registerLocale('fr', fr as any);

const DURATION_OPTIONS = [30, 45, 60, 90, 120];

const STATUS_MAP: Record<string, { label: string; color: ChipColor }> = {
    pending: { label: 'En attente', color: 'waiting' },
    accepted: { label: 'Accepté', color: 'success' },
    refused: { label: 'Refusé', color: 'danger' }
};

const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest}` : `${hours} h`;
};

const formatDay = (date: string) =>
    moment(date)
        .format('dddd D MMMM YYYY')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const renderDateHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }: any) => (
    <div className="dp-header">
        <button type="button" className="dp-nav" onClick={decreaseMonth} disabled={prevMonthButtonDisabled} aria-label="Mois précédent">
            <IoChevronBack />
        </button>
        <span className="dp-month">{capitalize(moment(date).format('MMMM YYYY'))}</span>
        <button type="button" className="dp-nav" onClick={increaseMonth} disabled={nextMonthButtonDisabled} aria-label="Mois suivant">
            <IoChevronForward />
        </button>
    </div>
);

const getPreference = (appointment: Appointment) => {
    const obs = (appointment.observation || '').toLowerCase();
    if (obs.includes('après-midi') || obs.includes('apres-midi') || obs.includes('après midi')) return 'Après-midi';
    if (obs.includes('matin')) return 'Matin';
    return moment(appointment.started_at).hour() < 13 ? 'Matin' : 'Après-midi';
};

type CardProps = {
    appointment: Appointment;
    processing: boolean;
    onAccept: (appointment: Appointment, started_at: string, ended_at: string) => void;
    onRefuse: (appointment: Appointment) => void;
};

function AppointmentCard({ appointment, processing, onAccept, onRefuse }: CardProps) {
    const initial = moment(appointment.started_at).toDate();
    const [selectedDate, setSelectedDate] = useState<Date | null>(initial);
    const [selectedTime, setSelectedTime] = useState<Date | null>(initial);
    const [duration, setDuration] = useState(() => {
        if (!appointment.ended_at) return 60;
        const diff = moment(appointment.ended_at).diff(moment(appointment.started_at), 'minutes');
        return DURATION_OPTIONS.includes(diff) ? diff : 60;
    });
    const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);

    const status = STATUS_MAP[appointment.status] ?? STATUS_MAP.pending;
    const isPending = appointment.status === 'pending';
    const isExit = appointment.type === 'exit';
    const cardTitle = isExit ? 'Demande de sortie du dispositif' : 'Demande de rendez-vous';
    const isScheduleValid = !!selectedDate && !!selectedTime;

    const accept = () => {
        if (!selectedDate || !selectedTime) return;
        const start = moment(selectedDate).set({
            hour: moment(selectedTime).hour(),
            minute: moment(selectedTime).minute(),
            second: 0,
            millisecond: 0
        });
        const started_at = start.format('YYYY-MM-DDTHH:mm:00');
        const ended_at = start.clone().add(duration, 'minutes').format('YYYY-MM-DDTHH:mm:00');
        onAccept(appointment, started_at, ended_at);
    };

    return (
        <ContentBlock className="appointment-request">
            <div className="card-header">
                <h2 className="title">{cardTitle}</h2>
                <Chip color={status.color} label={status.label} compact />
            </div>

            <p className="subtitle">Jour souhaité</p>
            <p>{formatDay(appointment.started_at)}</p>

            <p className="subtitle">Préférence</p>
            <p>{getPreference(appointment)}</p>

            {isExit && (
                <>
                    <p className="subtitle">Raison de la demande de sortie</p>
                    <p>{appointment.reason || '—'}</p>
                </>
            )}

            {appointment.status === 'accepted' && (
                <>
                    <span className="separator"></span>
                    <p className="subtitle">Rendez-vous planifié</p>
                    <p>
                        {moment(appointment.started_at).format('DD/MM/YYYY')} à {moment(appointment.started_at).format('HH:mm')}
                        {appointment.ended_at ? ` – ${moment(appointment.ended_at).format('HH:mm')}` : ''}
                    </p>
                </>
            )}

            {isPending && (
                <>
                    <span className="separator"></span>
                    <p className="subtitle">Planifier le rendez-vous</p>
                    <div className="schedule-form">
                        <label>
                            Date
                            <DatePicker
                                selected={selectedDate}
                                onChange={(date: Date | null) => setSelectedDate(date)}
                                renderCustomHeader={renderDateHeader}
                                dateFormat="dd/MM/yyyy"
                                locale="fr"
                                placeholderText="JJ/MM/AAAA"
                                className="schedule-input"
                            />
                        </label>
                        <label>
                            Heure de début
                            <DatePicker
                                selected={selectedTime}
                                onChange={(time: Date | null) => setSelectedTime(time)}
                                showTimeSelect
                                showTimeSelectOnly
                                timeIntervals={15}
                                timeCaption="Heure"
                                dateFormat="HH:mm"
                                timeFormat="HH:mm"
                                locale="fr"
                                placeholderText="HH:MM"
                                className="schedule-input"
                            />
                        </label>
                        <label>
                            Durée
                            <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                                {DURATION_OPTIONS.map((d) => (
                                    <option key={d} value={d}>
                                        {formatDuration(d)}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    <div className="actions">
                        <Button
                            label="Confirmer le rendez-vous"
                            variant="purple"
                            type="button"
                            disabled={processing || !isScheduleValid}
                            callback={accept}
                        />
                        <Button
                            label="Refuser"
                            variant="danger"
                            type="button"
                            disabled={processing}
                            callback={() => setIsRefuseModalOpen(true)}
                        />
                    </div>

                    <Modal
                        open={isRefuseModalOpen}
                        setClose={() => setIsRefuseModalOpen(false)}
                        title="Refuser la demande de rendez-vous"
                        yesText="Refuser"
                        noText="Annuler"
                        isValidateDestructive
                        onValidate={() => {
                            onRefuse(appointment);
                            setIsRefuseModalOpen(false);
                        }}
                    >
                        <p>Confirmez-vous le refus de cette demande de rendez-vous ? Cette action est définitive.</p>
                    </Modal>
                </>
            )}
        </ContentBlock>
    );
}

export default function AppointmentRequestsTab({ person }: { person: Person }) {
    const { appointments, isLoading, processingId, handleAccept, handleRefuse } = useAppointmentRequestsTabVM(person);

    if (isLoading) {
        return <section className="appointment-requests-tab">Chargement des demandes…</section>;
    }

    if (!appointments.length) {
        return (
            <section className="appointment-requests-tab">
                <ContentBlock>
                    <p>Aucune demande de rendez-vous pour cette personne soutenue.</p>
                </ContentBlock>
            </section>
        );
    }

    return (
        <section className="appointment-requests-tab">
            {appointments.map((appointment) => (
                <AppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    processing={processingId === appointment.id}
                    onAccept={handleAccept}
                    onRefuse={handleRefuse}
                />
            ))}
        </section>
    );
}
